// Registration-token handling — ported from auth.js _detectInviteToken /
// _clearInviteToken. A `?<param>=<token>` URL param is persisted to localStorage
// so it survives a reload and is attached to the next register() call.
//
// Two token types share the same plumbing:
//   - 'invite'  → `?invite=` … links a PWA account to an existing TG user
//                 (http_api/auth.py:register_invite_user). Storage key
//                 'pwa-chat-invite-token'.
//   - 'access'  → `?access=` … corporate access link that grants a pre-paid
//                 subscription on register / redeem (bd-r3r). Storage key
//                 'pwa-chat-access-token'.

export type TokenType = 'invite' | 'access'

const PARAM: Record<TokenType, string> = {
  invite: 'invite',
  access: 'access',
}

const STORAGE_KEY: Record<TokenType, string> = {
  invite: 'pwa-chat-invite-token',
  access: 'pwa-chat-access-token',
}

export function detectToken(type: TokenType): string | null {
  const params = new URLSearchParams(window.location.search)
  const urlToken = params.get(PARAM[type])
  const storageKey = STORAGE_KEY[type]
  const token = urlToken || localStorage.getItem(storageKey)
  if (token) localStorage.setItem(storageKey, token)
  // Strip the token from the address bar as soon as it's persisted so it can't
  // leak via screenshots / referrers / copy-paste (it lives in localStorage now).
  if (urlToken) {
    const url = new URL(window.location.href)
    url.searchParams.delete(PARAM[type])
    window.history.replaceState({}, '', url)
  }
  return token
}

export function getToken(type: TokenType): string | null {
  return localStorage.getItem(STORAGE_KEY[type])
}

export function clearToken(type: TokenType): void {
  localStorage.removeItem(STORAGE_KEY[type])
  if (window.location.search.includes(`${PARAM[type]}=`)) {
    const url = new URL(window.location.href)
    url.searchParams.delete(PARAM[type])
    window.history.replaceState({}, '', url)
  }
}

// --- Back-compat wrappers for the existing 'invite' callers ---

export function detectInviteToken(): string | null {
  return detectToken('invite')
}

export function getInviteToken(): string | null {
  return getToken('invite')
}

export function clearInviteToken(): void {
  clearToken('invite')
}

// --- 'access' wrappers (so call sites read symmetrically) ---

export function detectAccessToken(): string | null {
  return detectToken('access')
}

export function getAccessToken(): string | null {
  return getToken('access')
}

export function clearAccessToken(): void {
  clearToken('access')
}
